import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

export const Transformation = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

    return (
        <section
            data-testid="transformation-section"
            ref={ref}
            className="py-20 md:py-32 px-5 sm:px-8 lg:px-12 bg-[#0C0C0C]"
        >
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <div className="lg:col-span-6 order-2 lg:order-1">
                    <motion.div
                        style={{ y }}
                        className="relative rounded-[2rem] overflow-hidden border-4 border-[#F1F1EF] shadow-[0_35px_70px_-25px_rgba(211,160,167,0.4)] rotate-[-2deg]"
                    >
                        <img
                            src="/images/dark-transformation.png"
                            alt="Before and after — a business social profile transformed by Savv Media"
                            data-testid="transformation-image"
                            loading="lazy"
                            className="w-full h-auto block"
                        />
                    </motion.div>
                </div>
                <div className="lg:col-span-6 order-1 lg:order-2">
                    <Reveal>
                        <Eyebrow>Same business. Bigger impact. ♡</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-6 space-y-3">
                            <div className="flex items-center gap-4">
                                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#8C8A86] w-12">
                                    From
                                </span>
                                <span className="text-xl md:text-2xl font-medium text-[#A9A6A0] line-through decoration-[#C9DDEE] decoration-2">
                                    &ldquo;I have a business.&rdquo;
                                </span>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#D3A0A7] w-12">
                                    To
                                </span>
                                <span className="font-serif-ed text-3xl md:text-4xl text-[#F1F1EF]">
                                    &ldquo;I have a{" "}
                                    <span className="italic text-[#D3A0A7]">
                                        brand.
                                    </span>
                                    &rdquo;
                                </span>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-7 max-w-md text-base md:text-lg leading-relaxed text-[#A9A6A0]">
                            Your business already has a story. Savv helps you
                            make it impossible to ignore.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <a
                            href="#contact"
                            data-testid="transformation-cta"
                            className="group mt-8 inline-flex items-center gap-2 bg-[#D3A0A7] text-[#0C0C0C] font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-[#0C0C0C] hover:text-[#F1F1EF] transition-colors duration-300"
                        >
                            Transform My Brand
                            <ArrowRight
                                size={16}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </a>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
